import { SearchResponse } from '@algolia/client-search';
import AlgoliaData from '@module/Algolia/resources/js/search/interfaces/AlgoliaData';
import { FilterData, FilterDataItem } from '@phoenix/search/types';
import { get } from 'lodash-es';
import { getLocalizedPriceRangesFilterItems } from '@phoenix/helpers/facet-helper';

export const transformFacets = (indexName: string, results: SearchResponse<AlgoliaData>): FilterData[] => {
  if (!results.hasOwnProperty('facets')) {
    return [];
  }

  const filterNamesMapping = get(window.Phoenix.config.ALGOLIA.indexes, [indexName, 'filtersNameMapping'], {});

  return get(results, ['renderingContent', 'facetOrdering', 'facets', 'order'], [])
    .map((facet: string) => {
      let config = get(window.Phoenix.config.ALGOLIA.indexes, [indexName, 'filters', facet]);
      const facetKey = getLocalizedFacetKey(results.facets, facet, get(config, 'valuePath', ''));
      const component = get(config, 'component', window.Phoenix.config.ALGOLIA.defaultFilterComponent);

      if (isRangeFacetComponent(component)) {
        config = getRangeFacetConfig(results, facet, facetKey, config);
      }

      if (isPriceRangeFacet(facet)) {
        config = {
          ...config,
          uniqueValue: true,
          hasRange: true,
        };
      }

      const filterName = (facetKey.split('.').shift() ?? '').replaceAll('pp_', '');

      return {
        component,
        label: window.Phoenix.i18n.t(`Filters.label.${facet}`),
        // Having dots in facet id causes issues because it can be interpreted as an object path
        id: facetKey.replaceAll('.', '--'),
        name: filterNamesMapping[filterName.toLowerCase()] ?? filterName,
        items: transformFacetItems(facet, facetKey, results, component),
        ...config,
      };
    })
    .filter((item) => item !== null && item.items.length >= (isRangeFacetComponent(item.component) ? 2 : 1));
};

const transformFacetItems = (
  facet: string,
  facetKey: string,
  results: SearchResponse<AlgoliaData>,
  component: string,
): FilterDataItem[] => {
  const facetItems = get(results, ['facets', facetKey], {});
  const sortType = get(results, ['renderingContent', 'facetOrdering', 'values', facet, 'sortRemainingBy']);
  const hiddenValues = get(results, ['renderingContent', 'facetOrdering', 'values', facet, 'hide'], []);
  const pinnedValues = get(results, ['renderingContent', 'facetOrdering', 'values', facet, 'order'], []);

  const items = Object.keys(facetItems)
    .filter(
      (key) =>
        (sortType !== 'hidden' && !hiddenValues.includes(key)) || (sortType === 'hidden' && pinnedValues.includes(key)),
    )
    .map((key) => ({ value: key, count: facetItems[key] }))
    .map((item) => {
      const data: FilterDataItem = {
        label: facet.endsWith('._id') ? window.Phoenix.i18n.t(item.value) : item.value,
        value: item.value,
        valueName: facet.endsWith('._id') ? item.value.split('.').pop() : item.value,
        count: item.count,
      };

      if (isColorFacetComponent(component)) {
        data.valueName = get(
          window.Phoenix.config.ALGOLIA.colorsMapping,
          item.value,
          window.Phoenix.config.ALGOLIA.defaultColor,
        );
      }

      return data;
    })
    .sort((a, b) => {
      const pinnedIndexA = pinnedValues.indexOf(a.value);
      const pinnedIndexB = pinnedValues.indexOf(b.value);

      // If both values are pinned
      if (pinnedIndexA !== -1 && pinnedIndexB !== -1) {
        return pinnedIndexA - pinnedIndexB;
      }

      // If only one value is pinned
      if (pinnedIndexA !== -1 || pinnedIndexB !== -1) {
        return pinnedIndexB - pinnedIndexA;
      }

      switch (sortType) {
        case 'alpha':
          return a.label.localeCompare(b.label);
        case 'count':
          return b.count - a.count;
        default:
          return 0;
      }
    });

  if (isPriceRangeFacet(facet)) {
    const rangesConfig = get(window.Phoenix.config.ALGOLIA, ['extraData', 'priceRangesConfig', 'ranges'], []);
    const currencyByCountry = get(
      window.Phoenix.config.ALGOLIA,
      ['extraData', 'priceRangesConfig', 'countriesWithCurrency'],
      [],
    );
    const localizedItems = getLocalizedPriceRangesFilterItems(rangesConfig, currencyByCountry);
    return items.map((item) => ({
      ...item,
      ...localizedItems[item.value],
    }));
  }

  return items;
};

const isRangeFacetComponent = (component: string): boolean => component === 'range';

const isColorFacetComponent = (component: string): boolean => component === 'color';

const isPriceRangeFacet = (facet: string): boolean => facet === 'priceRanges';

const getRangeFacetConfig = (
  results: SearchResponse<AlgoliaData>,
  facet: string,
  facetKey: string,
  config: object,
): object => {
  let symbol = '';
  const symbolPath = get(config, 'symbolPath');
  const symbolKey = symbolPath ? getLocalizedFacetKey(results.facets, facet, get(config, 'symbolPath', '')) : null;

  if (symbolKey) {
    symbol = Object.keys(get(results, ['facets', symbolKey], {})).shift() ?? '';
  }

  const range = {
    min: get(results, ['facets_stats', facetKey, 'min'], 0),
    max: get(results, ['facets_stats', facetKey, 'max'], 0),
  };

  return {
    ...config,
    options: {
      format: {
        precision: null,
        thousand: ' ',
        decimal: ',',
        symbol,
      },
      noUiSliderOptions: {
        start: [range.min, range.max],
        range,
        connect: [false, true, false],
        tooltips: true,
        step: Math.max(1, Math.ceil(range.max / 1000)),
        snap: false,
      },
    },
  };
};

const getLocalizedFacetKey = (facets: object, facet: string, path: string): string => {
  for (const key of [
    window.Phoenix.laravelLocale,
    window.Phoenix.laravelLanguage,
    window.Phoenix.currentCountry,
    window.Phoenix.defaultCountryCode,
  ]) {
    const fullKey = [facet, key, path].filter((value) => value !== '').join('.');
    if (facets.hasOwnProperty(fullKey)) {
      return fullKey;
    }
  }

  return facet;
};
